import { useMatches } from '@remix-run/react';

import type { GoogleFontEntryType } from '~/services/fonts/cache';

type RenderableFontsCssMatch = { data: { renderableFontsCss: GoogleFontEntryType[] } };

const hasRenderableFonts = (match: Partial<RenderableFontsCssMatch>) => {
  return !!match.data?.renderableFontsCss;
};

export const FontStyles = () => {
  const matches = useMatches();
  const fontMatches = matches.filter((match) => hasRenderableFonts(match as Partial<RenderableFontsCssMatch>));
  if (!fontMatches.length) return null;

  // de-duplicate fonts by URL
  const renderableFontsCss = Array.from(
    new Map(
      fontMatches
        .flatMap((match) => (match as RenderableFontsCssMatch).data.renderableFontsCss)
        .map((font) => [font.url, font]),
    ).values(),
  );
  return renderableFontsCss.map(({ url, css }: GoogleFontEntryType) => (
    <style id={getFontId(url)} key={url}>
      {css}
    </style>
  ));
};

function getFontId(url: string) {
  // Custom fonts already have descriptive IDs like "custom-font-operator-mono-ssm"
  if (url.startsWith('custom-font-')) {
    return url;
  }

  // Google Fonts use the URL to determine the ID
  return `google-font-${getFontFamily(url)}`;
}

function getFontFamily(url: string) {
  try {
    const family = new URL(url, 'https://fonts.googleapis.com').searchParams.get('family')?.split(':')[0] || 'unknown';
    return family.replace(/[^a-zA-Z0-9]/g, '-');
  } catch {
    return 'unknown';
  }
}
